import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Alert, Box, Button, Divider, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ChevronRightRounded as ChevronRightRoundedIcon } from '@mui/icons-material';
import colors from '../../assets/theme/colors';
import { CARE_PROFILE_ROUTE } from '../../constants/profileRoutes';
import { buildCaptureDestinationReceipt } from './captureDestinationReceiptModel';
import { organizeCapturedCareSource } from './careSourceProcessingClient';

const ReceiptItems = ({ items = [] }) => (
  <Stack component="ul" spacing={0.35} sx={{ m: 0, pl: 2.25 }}>
    {items.map((item) => (
      <Typography
        component="li"
        key={item}
        sx={{ color: alpha(colors.semantic.ink, 0.76), fontSize: '0.8rem', lineHeight: 1.4 }}
      >
        {item}
      </Typography>
    ))}
  </Stack>
);

const DestinationHeading = ({ children }) => (
  <Typography sx={{ color: colors.semantic.ink, fontSize: '0.84rem', fontWeight: 800 }}>
    {children}
  </Typography>
);

const CaptureDestinationReceipt = ({
  sources = [],
  childId = '',
  captureKey = '',
  profileReturnTo = '/dashboard',
  profileReturnState = null,
}) => {
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState('');
  const receipt = useMemo(() => buildCaptureDestinationReceipt(sources), [sources]);
  const partialSource = sources.find((source) => (
    String(source.status || source.metadata?.careSourceProcessing?.status || '').trim() !== 'reviewed'
    &&
    source.metadata?.careSourceProcessing?.profileExtractionRecovery?.status === 'partial'
  ));
  const handleRetry = async () => {
    if (!partialSource?.id || retrying) return;
    setRetrying(true);
    setRetryError('');
    try {
      const result = await organizeCapturedCareSource({
        sourceNoteId: partialSource.id,
        options: { retry: true, profileExtractionRetry: true },
      });
      if (result?.ok === false) throw new Error("We couldn't organize these Profile details.");
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('captureez:timeline-refresh', {
          detail: { childId, sourceNoteId: partialSource.id },
        }));
      }
    } catch (error) {
      setRetryError(error?.message || "We couldn't organize these Profile details.");
    } finally {
      setRetrying(false);
    }
  };
  if (!receipt.profileSections.length && !partialSource) return null;

  return (
    <Box sx={{ py: 1.25 }}>
      {partialSource ? (
        <Alert
          severity="warning"
          action={(
            <Button color="inherit" size="small" disabled={retrying} onClick={handleRetry}>
              {retrying ? 'Organizing…' : 'Try organizing again'}
            </Button>
          )}
          sx={{ mb: 1.1 }}
        >
          Some profile details could not be organized. Your original note is saved.
        </Alert>
      ) : null}
      {retryError ? <Alert severity="error" sx={{ mb: 1.1 }}>{retryError}</Alert> : null}
      {receipt.profileSections.length ? (
        <>
      <Typography sx={{ color: alpha(colors.semantic.ink, 0.58), fontSize: '0.74rem', fontWeight: 800, mb: 0.9, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        Where this was added
      </Typography>
      <Stack spacing={1.1} divider={<Divider flexItem />}>
        {receipt.profileSections.map((section) => (
          <Stack key={section.chapterId} spacing={0.55}>
            <DestinationHeading>{section.label}</DestinationHeading>
            <ReceiptItems items={section.items} />
            <Button
              component={RouterLink}
              to={CARE_PROFILE_ROUTE}
              state={{
                childId,
                profileChapterViewId: section.profileViewId,
                profileReturnTo,
                profileReturnState: profileReturnState || {
                  selectedChildId: childId,
                  openCaptureDetailsKey: captureKey,
                },
              }}
              endIcon={<ChevronRightRoundedIcon />}
              sx={{ alignSelf: 'flex-start', minHeight: 0, p: 0, color: colors.semantic.sageDeep, fontSize: '0.8rem', fontWeight: 800, textTransform: 'none' }}
            >
              Open {section.label}
            </Button>
          </Stack>
        ))}
      </Stack>
        </>
      ) : null}
    </Box>
  );
};

export default CaptureDestinationReceipt;
